import React from "react";

import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import SchedulingDateSelector from "./SchedulingDateSelector";
import FastAPI from "../../../api/FastAPI";
import useFAST from "../../../contexts/FASTContext";
import useTimebounds from "../../../hooks/useTimebounds";
import computeAdsObjectiveCompletion from "../calendar/adsObjectiveCompletion";
import ObjectiveComponent from "../ObjectiveComponent";

import type { JSX } from "react";

import type { FASTChannelRoutesParams } from "../../routes/FASTChannelRoutes";

interface SchedulingOverviewProps {
  isEditMode: boolean;
}

function SchedulingOverview({
  isEditMode,
}: SchedulingOverviewProps): JSX.Element {
  const { state } = useFAST();
  const { t } = useTranslation("editor-fast-channel");
  const { timelineScheduleSelected, schedules } = state;
  const { channelId } = useParams<FASTChannelRoutesParams>();
  const { currentDay } = useTimebounds();

  const scheduleSelected = schedules.find(
    (schedule) => schedule.uuid === timelineScheduleSelected,
  );

  const getScheduleKpisQuery = FastAPI.generateQuery({
    fn: FastAPI.getScheduleKpis,
    params: [
      {
        channelId,
        scheduleId: timelineScheduleSelected ?? "",
        day: currentDay,
      },
    ],
  });

  const { data: dailyChannelKpis } = useQuery({
    ...getScheduleKpisQuery,
    enabled: Boolean(channelId && timelineScheduleSelected),
    keepPreviousData: true,
  });

  const ads = {
    duration: dailyChannelKpis?.data.ads_duration,
    completion: computeAdsObjectiveCompletion(dailyChannelKpis),
    objective: scheduleSelected?.daily_ads_objective,
  };

  const program = {
    duration: dailyChannelKpis?.data.programs_duration,
    completion: [{ value: dailyChannelKpis?.data.programs_completion ?? 0 }],
    objective: scheduleSelected?.daily_programs_objective,
  };

  return (
    <div className="mb-[0.78125rem] flex items-center gap-[0.78125rem]">
      <ObjectiveComponent
        borderColor={scheduleSelected?.color}
        completion={program.completion}
        current={program?.duration}
        objective={program?.objective}
        subTitle={scheduleSelected?.name}
        title={t("daily_program_objective")}
      />
      <SchedulingDateSelector isEditMode={isEditMode} />
      <ObjectiveComponent
        borderColor={scheduleSelected?.color}
        completion={ads.completion}
        current={ads?.duration}
        objective={ads?.objective}
        subTitle={scheduleSelected?.name}
        title={t("daily_ads_objective")}
      />
    </div>
  );
}

export default SchedulingOverview;
